import bigInt from "big-integer";
import { cloneObject, getChildUnitOf, getTimeUnitOptions } from ".";
import {
  gregorianCalendar,
  faerunCalendar,
  greyhawkCalendar,
  golarionCalendar,
  customCalendar,
} from "./_calendars";

export const calendarTemplates = {
  gregorian: gregorianCalendar,
  faerun: faerunCalendar,
  greyhawk: greyhawkCalendar,
  golarion: golarionCalendar,
  custom: customCalendar,
};

export const calendarOptions = [
  //... add any other calendars that match the world, then use the pre-defined templates below
  { name: "Gregorian (Standard Western)", value: "gregorian" },
  { name: "Forgotten Realms (Dungeons & Dragons)", value: "faerun" },
  { name: "Greyhawk (Dungeons & Dragons)", value: "greyhawk" },
  { name: "Golarion Absalom (Pathfinder)", value: "golarion" },
  { name: "Custom (Empty calendar)", value: "custom" },
];
/**
 *
 * @param {Object} calendar Calendar object
 * @param {String} target The format of the numbers (determines calculation "direction")
 * @returns UserCalendar (formatted for simple values, 'year' = 365 days) || DataCalendar (formatted for Chronos, 'year' = 31,536,000 seconds)
 */
export function parseCalendar(calendar, target = "api") {
  //First, copy the calendar data so changes don't mutate the original
  const calendarData = cloneObject(calendar);
  //Compose the returned object using the Calendar data as defaults.
  const ret = {
    ...calendarData,
    time: { ...calendarData.time },
    months: [...calendarData.months],
  };

  // Temporary storage for parsing. This prevents the need for more convoluted logic to achieve similar results (easier to read, hopefully)
  let durations = {
    minute: "",
    hour: "",
    day: "",
    year: "",
    decade: "",
    century: "",
    millenium: "",
    myriad: "",
    epoch: "",
    aeon: "",
  };

  //Order of operations are important, so units must be calculated in a set order.
  let units = [
    "minute",
    "hour",
    "day",
    "year",
    "decade",
    "century",
    "millenium",
    "myriad",
    "epoch",
    "aeon",
  ];

  //Destructure the time settings from the Calendar
  const { time, months } = calendarData;

  //Determine if we should apply mult or div based on the target.
  let operation = target === "api" ? "times" : "divide";
  //Start at the end of the array if the target is the user form
  let startIndex = target === "api" ? 0 : units.length - 1;

  /* Note: This could be simplified by using a conditional Array.reverse() (if target === 'form') */

  for (
    let i = startIndex;
    i < units.length && i >= 0;
    i = startIndex > 0 ? i - 1 : i + 1
  ) {
    //Get name of the unit. This is used as an accessor below, and the child unit is derived by this value.
    const keyName = units[i];
    //Get the actual data of the time unit (names, flags, etc.)
    const unit = time[keyName];
    //Derive the child unit of the current unit (ie, 'years' are based off 'days', 'decades' (and higher) are based off years)
    const childName = getChildUnitOf(keyName);
    //We need different sources depending on the target
    const calcObj = target === "api" ? durations : time;
    //Helper function to return Calendar (or Time) duration
    function getValue(inputVal) {
      return bigInt(
        //The input can be string or object. If it's an object, we simply access the [duration] value (converting to 'form')
        typeof inputVal[childName] === "string"
          ? inputVal[childName]
          : inputVal[childName].duration
      );
    }

    switch (keyName) {
      case "minute": {
        // console.log('KEY: ', keyName);
        durations[keyName] = bigInt(parseUnitValue(unit.duration)).toString();
        break;
      }
      case "hour": {
        // console.log('KEY: ', keyName);
        durations[keyName] = bigInt(parseUnitValue(unit.duration))
          [operation](getValue(calcObj))
          .toString();
        break;
      }
      case "day": {
        // console.log('KEY: ', keyName);
        durations[keyName] = bigInt(parseUnitValue(unit.duration))
          [operation](getValue(calcObj))
          .toString();

        break;
      }
      case "year": {
        // console.log('KEY: ', keyName);
        const daysInYear = months.reduce((t, month) => {
          return t + parseInt(month.days, 10);
        }, 0);
        // console.log('--- Days In Year', daysInYear);
        const result = bigInt(
          target === "api" ? daysInYear : parseUnitValue(unit.duration)
        )
          [operation](getValue(calcObj))
          .toString();
        durations[keyName] = result;
        // console.log('--- Year in Seconds', result);
        break;
      }
      case "decade":
      case "century":
      case "millenium":
      case "myriad":
      case "epoch":
      case "aeon": {
        // console.log('KEY: ', keyName);
        durations[keyName] = bigInt(parseUnitValue(unit.duration))
          [operation](getValue(calcObj))
          .toString();
        break;
      }
    }
  }

  //Update the actual durations (either display(target: form) or raw(target: api))
  Object.keys(durations).forEach((unitName) => {
    ret.time[unitName].duration = durations[unitName];
  });

  //All done with the calculations, return the formatted Calendar.
  return ret;
}

//Utility to remove all commas from numerical strings (since libraries/packages used for
//text-masking return the formatted value).
function parseUnitValue(str) {
  // console.log(str)
  str = str.replace(/\,/gim, "");
  return str;
}

export function createTimeUnitOptions(calendar) {
  const defaultUnits = getTimeUnitOptions();
  let ret = defaultUnits.map((unit) => {
    return { name: calendar.time[unit.value].plural, value: unit.value };
  });
  return [...ret];
}
